import { useState, useEffect, useRef, useCallback } from 'react'
import { Send, Paperclip, ArrowLeft, MessageSquare, Lock, Loader2, X } from 'lucide-react'
import { chatApi } from '../lib/chatApi'
import { checkRateLimit } from '../lib/rateLimit'
import { useAuth } from '../hooks/useAuth'

export default function ChatPanel({ conversationId, onBack, agentEmail }) {
  const [messages, setMessages] = useState([])
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [attachment, setAttachment] = useState(null)
  const fileRef = useRef(null)
  const scrollRef = useRef(null)
  const { user, profile } = useAuth()
  const senderType = profile?.role === 'founder' ? 'founder' : 'agent'
  const senderEmail = user?.email

  const loadMessages = useCallback(async () => {
    if (!conversationId) return
    const { data, error } = await chatApi.listMessages(conversationId)
    if (error) { setError(error); setLoading(false); return }
    setMessages(data)
    setLoading(false)
    if (user?.id) await chatApi.markRead(conversationId, user.id)
  }, [conversationId, user?.id])

  useEffect(() => {
    loadMessages()
    const interval = setInterval(loadMessages, 5000)
    return () => clearInterval(interval)
  }, [loadMessages])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const handleSend = async (e) => {
    e?.preventDefault()
    if (!body.trim() && !attachment) return

    const rl = checkRateLimit('chat', user?.id || 'anon')
    if (!rl.allowed) {
      setError('You are sending messages too fast. Please wait a moment.')
      return
    }

    setSending(true)
    setError(null)
    const msgBody = body.trim()
    setBody('')

    let attachmentUrl = null
    let attachmentName = null
    if (attachment) {
      attachmentName = attachment.name
      attachmentUrl = `pending:${attachment.name}`
    }

    const { data, error: sendError } = await chatApi.sendMessage(conversationId, {
      senderType,
      senderEmail,
      body: msgBody,
      attachmentUrl,
      attachmentName,
    })

    if (sendError) {
      setError(sendError)
      setBody(msgBody)
    } else {
      setMessages((prev) => [...prev, data])
      setAttachment(null)
      if (fileRef.current) fileRef.current.value = ''
    }

    setSending(false)
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      setError('File must be under 10MB')
      return
    }
    setAttachment(file)
  }

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 sm:px-6 py-4 border-b border-neutral-200 bg-surface flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <button onClick={onBack} className="text-neutral-400 hover:text-ink shrink-0">
              <ArrowLeft size={18} />
            </button>
          )}
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink truncate">Chat with {agentEmail || 'Agent'}</h2>
            <p className="text-[11px] text-muted">{messages.length} messages</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-200 shrink-0">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> Encrypted
        </span>
      </div>

      {error && (
        <div className="px-4 py-2 bg-rose-50 border-b border-rose-200 text-xs text-rose-700">
          {error}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-neutral-50">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={20} className="animate-spin text-neutral-400" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center py-10">
            <MessageSquare size={28} className="text-neutral-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-ink">No messages yet</p>
            <p className="text-xs text-muted mt-1">Start the conversation by sending a message below.</p>
          </div>
        )}
        {messages.map((msg) => {
          const isMine = msg.sender_type === senderType && msg.sender_email === senderEmail
          return (
            <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] sm:max-w-[60%] ${isMine ? 'bg-brand text-white' : 'bg-white border border-neutral-200 text-ink'} px-4 py-3 rounded-lg`}>
                {!isMine && (
                  <p className="text-[10px] font-semibold text-muted mb-1">{msg.sender_email || msg.sender_type}</p>
                )}
                {msg.body && <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>}
                {msg.attachment_name && (
                  <div className={`mt-2 flex items-center gap-1.5 text-xs ${isMine ? 'text-white/80' : 'text-brand'}`}>
                    <Paperclip size={12} /> {msg.attachment_name}
                  </div>
                )}
                <p className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-neutral-400'}`}>
                  {new Date(msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {attachment && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 flex items-center justify-between">
          <span className="text-xs text-amber-800 flex items-center gap-1.5">
            <Paperclip size={12} /> {attachment.name}
          </span>
          <button onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = '' }} className="text-amber-600 hover:text-amber-800">
            <X size={14} />
          </button>
        </div>
      )}

      <form onSubmit={handleSend} className="shrink-0 p-4 border-t border-neutral-200 bg-surface">
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={handleFileChange} accept="image/*,.pdf,.doc,.docx" />
          <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost p-2.5 shrink-0" disabled={sending || uploading}>
            <Paperclip size={16} />
          </button>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
            className="input-flat flex-1 resize-none min-h-[44px] max-h-32"
            placeholder="Type a message..."
            rows={1}
            disabled={sending}
          />
          <button type="submit" className="btn-primary p-2.5 shrink-0" disabled={sending || (!body.trim() && !attachment)}>
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </form>
    </div>
  )
}

export function ChatLockedState({ reason }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mb-4">
        <Lock size={28} className="text-neutral-400" />
      </div>
      <h3 className="text-sm font-semibold text-ink mb-2">Chat Locked</h3>
      <p className="text-xs text-muted max-w-sm">{reason || 'Chat will be available once an Agent has accepted your request.'}</p>
    </div>
  )
}
